/**
 * Project List Component
 *
 * Displays projects in card grid with filtering and actions
 * Similar pattern to RepositoryList
 *
 * Features:
 * - Card grid layout (responsive)
 * - Status badges and budget utilization
 * - Actions: edit, delete, view costs
 * - Filter by status (active, archived, on_hold, completed)
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Card, Badge, Button, Spinner, Alert, Select, Label } from 'flowbite-react';
import { HiPencil, HiTrash, HiCurrencyDollar, HiChartBar, HiFolderOpen, HiCog } from 'react-icons/hi';

import { listProjects, deleteProject, type Project, type ProjectWithCosts } from '../api/projects';

interface ProjectListProps {
  clientId: string;
  companyId: string;
  onEditProject?: (project: Project) => void;
  onConfigureProject?: (project: Project) => void;
  onViewCosts?: (projectId: string) => void;
  onRefresh?: () => void;
  showCosts?: boolean;
}

export const ProjectList: React.FC<ProjectListProps> = ({
  clientId,
  companyId,
  onEditProject,
  onConfigureProject,
  onViewCosts,
  onRefresh,
  showCosts = true
}) => {
  const [projects, setProjects] = useState<(Project | ProjectWithCosts)[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deletingProjects, setDeletingProjects] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (clientId && companyId) {
      fetchProjects();
    }
  }, [clientId, companyId, statusFilter, showCosts]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await listProjects(clientId, companyId, {
        status: statusFilter !== 'all' ? statusFilter as any : undefined,
        include_costs: showCosts
      });

      if (!response.success) {
        throw new Error(response.message || 'API returned unsuccessful response');
      }

      setProjects(response.data?.Records || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load projects';
      console.error('[ProjectList] Error fetching projects:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (project: Project) => {
    if (!window.confirm(`Are you sure you want to delete project "${project.Project_Name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingProjects(prev => new Set(prev).add(project.Project_ID));

      const response = await deleteProject(clientId, companyId, project.Project_ID);

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete project');
      }

      await fetchProjects();
      onRefresh?.();
    } catch (err) {
      console.error('[ProjectList] Error deleting project:', err);
      alert(`Failed to delete project: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setDeletingProjects(prev => {
        const newSet = new Set(prev);
        newSet.delete(project.Project_ID);
        return newSet;
      });
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'completed':
        return 'info';
      case 'on_hold':
        return 'warning';
      case 'archived':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const getBudgetUtilizationColor = (utilization?: number) => {
    if (!utilization) return 'gray';
    if (utilization < 50) return 'success';
    if (utilization < 75) return 'warning';
    if (utilization < 90) return 'failure';
    return 'dark';
  };

  const filteredProjects = useMemo(() => {
    return projects;
  }, [projects]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert color="failure">
        <div>
          <p className="font-semibold">Error loading projects</p>
          <p className="text-sm mt-1">{error}</p>
          <Button
            size="xs"
            color="light"
            className="mt-2"
            onClick={fetchProjects}
          >
            Retry
          </Button>
        </div>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div>
            <Label htmlFor="status-filter" value="Filter by Status" className="sr-only" />
            <Select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-48"
            >
              <option value="all">All Projects</option>
              <option value="active">Active</option>
              <option value="on_hold">On Hold</option>
              <option value="completed">Completed</option>
              <option value="archived">Archived</option>
            </Select>
          </div>
        </div>

        <div className="text-sm text-gray-600">
          {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Project Grid */}
      {filteredProjects.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <HiFolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No projects found</p>
            <p className="text-sm text-gray-400">
              {statusFilter !== 'all'
                ? `No ${statusFilter} projects. Try changing the filter.`
                : 'Click "Create Project" to add your first project'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProjects.map((project) => {
            const projectWithCosts = 'budget_utilization_pct' in project
              ? project as ProjectWithCosts
              : undefined;

            return (
              <Card key={project.Project_ID}>
                <div className="flex flex-col h-full">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {project.Project_Name}
                      </h3>
                      <p className="text-xs text-gray-500 font-mono mt-1 truncate">
                        {project.Project_ID}
                      </p>
                    </div>
                    <HiFolderOpen className="w-6 h-6 text-gray-400 ml-2 flex-shrink-0" />
                  </div>

                  {/* Description */}
                  {project.Project_Description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {project.Project_Description}
                    </p>
                  )}

                  {/* Badges */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <Badge color={getStatusBadgeColor(project.Project_Status)}>
                      {project.Project_Status.replace('_', ' ').toUpperCase()}
                    </Badge>
                    {projectWithCosts?.Budget_USD && projectWithCosts.budget_utilization_pct !== undefined && (
                      <Badge color={getBudgetUtilizationColor(projectWithCosts.budget_utilization_pct)}>
                        <HiChartBar className="w-3 h-3 mr-1 inline" />
                        {projectWithCosts.budget_utilization_pct.toFixed(0)}%
                      </Badge>
                    )}
                  </div>

                  {/* Budget Info */}
                  {showCosts && projectWithCosts && (
                    <div className="mb-3 p-2 bg-gray-50 rounded border border-gray-200">
                      {projectWithCosts.Budget_USD && (
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600">Budget:</span>
                            <span className="font-mono font-semibold">
                              ${projectWithCosts.Budget_USD.toLocaleString()}
                            </span>
                          </div>
                          {projectWithCosts.total_cost !== undefined && (
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-600">Spent:</span>
                              <span className="font-mono font-semibold text-red-600">
                                ${projectWithCosts.total_cost.toFixed(2)}
                              </span>
                            </div>
                          )}
                          {projectWithCosts.Budget_Period && (
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-600">Period:</span>
                              <span className="capitalize">{projectWithCosts.Budget_Period}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {!projectWithCosts.Budget_USD && (
                        <p className="text-xs text-gray-500">No budget set</p>
                      )}
                    </div>
                  )}

                  {/* Project Metadata */}
                  <div className="text-xs text-gray-500 space-y-1 mb-3">
                    {project.Owner_Email && (
                      <div className="truncate">
                        <span className="font-medium">Owner:</span> {project.Owner_Email}
                      </div>
                    )}
                    {project.Cost_Center && (
                      <div className="truncate">
                        <span className="font-medium">Cost Center:</span> {project.Cost_Center}
                      </div>
                    )}
                    {project.Start_Date && (
                      <div>
                        <span className="font-medium">Started:</span>{' '}
                        {new Date(project.Start_Date).toLocaleDateString()}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 mt-auto">
                    {onConfigureProject && (
                      <Button
                        size="sm"
                        color="blue"
                        onClick={() => onConfigureProject(project)}
                      >
                        <HiCog className="w-4 h-4 mr-2" />
                        Configure
                      </Button>
                    )}
                    {showCosts && onViewCosts && (
                      <Button
                        size="sm"
                        color="light"
                        onClick={() => onViewCosts(project.Project_ID)}
                      >
                        <HiCurrencyDollar className="w-4 h-4 mr-2" />
                        View Costs
                      </Button>
                    )}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        color="light"
                        className="flex-1"
                        onClick={() => onEditProject?.(project)}
                      >
                        <HiPencil className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        color="failure"
                        onClick={() => handleDeleteProject(project)}
                        disabled={deletingProjects.has(project.Project_ID)}
                      >
                        {deletingProjects.has(project.Project_ID) ? (
                          <Spinner size="sm" />
                        ) : (
                          <HiTrash className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProjectList;
